<template>
  <div>
    <div class="air__utils__heading">
      <div class="row">
        <div class="col-12">
          <h5>Lesson Plan Teacher Report</h5>
        </div>
      </div>
    </div>

    <div class="row justify-content-center" v-if="pagepermission.viewable">
      <div class="col-xl-12">
        <!-- Filters -->
        <div class="row align-items-center mb-2">
          <div class="col-md-2 col-12 mb-2">
            <a-select
              size="large"
              v-model="selectedSession"
              placeholder="Select Session"
              style="width: 100%"
              @change="handleSessionChange"
            >
              <template v-for="session in schoolSessions">
                <a-select-option :key="session.id" :value="session.id">
                  {{ session.name }}
                  <small class="text-primary ml-2" v-if="session.is_current">Current</small>
                </a-select-option>
              </template>
            </a-select>
          </div>

          <div class="col-md-4 col-12 mb-2">
            <a-spin :spinning="teacherSpinning">
              <a-select
                show-search
                option-filter-prop="children"
                size="large"
                v-model="selectedTeacher"
                placeholder="All Teachers"
                style="width: 100%"
                @change="handleTeacherSelect"
              >
                <a-select-option :value="undefined">All Teachers</a-select-option>
                <template v-for="teacher in teachers">
                  <a-select-option :key="teacher.id" :value="teacher.id">
                    {{ teacher.first_name }} {{ teacher.last_name }} ({{ teacher.staff_id }})
                  </a-select-option>
                </template>
              </a-select>
            </a-spin>
          </div>

          <div class="col-md-4 col-12 mb-2"></div>
        </div>

        <!-- Table -->
        <div class="card">
          <div class="card-body">
            <teacher-report-table
              :data="data"
              :loading="loading"
              @updateTable="handleUpdateTable"
              @viewDetails="handleViewDetails"
            />
          </div>
        </div>

        <!-- Pagination -->
        <div class="text-right">
          <a-pagination
            showSizeChanger
            @showSizeChange="onShowSizeChange"
            :total="meta.total"
            :pageSize.sync="pageSize"
            v-model="current"
            @change="onChange"
          />
        </div>

        <TeacherLogsModal
          :visible="logsModalVisible"
          :teacher="selectedTeacherForModal"
          @close="logsModalVisible = false"
        />
      </div>
    </div>

    <div class="row justify-content-center" v-else>
      <a-spin :spinning="permissionCheckSpinning">
        <p class="error" v-if="!permissionCheckSpinning">
          You don't have permission to view this report.
        </p>
      </a-spin>
    </div>
  </div>
</template>

<script>
import TeacherReportTable from '@/components/lessonplan/report/teacherProgressReport/Table'
import TeacherLogsModal from '@/components/lessonplan/report/teacherProgressReport/Modal/TeacherLogsModal'
import { find } from 'lodash'

export default {
  components: { TeacherReportTable, TeacherLogsModal },

  data() {
    return {
      loading: true,
      data: [],
      meta: { total: 0 },
      schoolSessions: [],
      schoolClasses: [],
      teachers: [],
      selectedSession: undefined,
      selectedTeacher: undefined,
      teacherSpinning: false,
      permissionCheckSpinning: true,
      pagepermission: {},
      current: 1,
      pageSize: 20,
      sorterField: null,
      sorterOrder: null,
      filters: {},
      logsModalVisible: false,
      selectedTeacherForModal: null,
    }
  },

  computed: {
    currentSession() {
      return find(this.schoolSessions, ['is_current', 1])
    },
  },

  mounted() {
    this.$roleutility.checkpermission('lessonplan', 23)
      .then(data => {
        this.pagepermission = data.pagePermission
        this.permissionCheckSpinning = false
        if (this.pagepermission.viewable) {
          this.getSessionsAndInitialLoad()
        }
      })
      .catch(() => {
        this.permissionCheckSpinning = false
        this.pagepermission.viewable = false
      })
  },

  methods: {
    getSessionsAndInitialLoad() {
      this.$http.get('/api/session').then(({ data }) => {
        this.schoolSessions = data.data
        const currentSession = this.currentSession
        this.selectedSession = currentSession ? currentSession.id : (data.data.length > 0 ? data.data[0].id : undefined)
        if (this.selectedSession) {
          this.getTeachers()
          this.getReportData()
        }
      })
    },

    getTeachers() {
      let teachers = []
      let currentPage = 1
      let totalPages = 1

      // First, get the first page
      this.$http.get('/api/teacher/', { params: { page: currentPage } })
        .then(firstPageResponse => {
          const firstPageData = firstPageResponse.data
          teachers = [...teachers, ...firstPageData.data]

          // Check pagination metadata
          if (firstPageData.meta && firstPageData.meta.last_page) {
            totalPages = firstPageData.meta.last_page
          }

          if (totalPages > 1) {
            const pagePromises = []

            for (let page = 2; page <= totalPages; page++) {
              pagePromises.push(
                this.$http.get('/api/teacher/', { params: { page } })
              )
            }

            // Fetch all remaining pages in parallel
            return Promise.all(pagePromises).then(responses => {
              responses.forEach(response => {
                teachers = [...teachers, ...response.data.data]
              })
              return teachers
            })
          } else {
            return teachers
          }
        })
        .then(teachersResult => {
          this.teachers = teachersResult
          console.log(`Loaded ${this.teachers.length} teachers from ${totalPages} pages`)
        })
        .catch(error => {
          console.error('Failed to fetch teachers', error)
          this.$notification.error({
            message: 'Failed to load teachers',
            description: 'Please try again later.',
          })
        })
    },

    getReportData(isFilter = false) {
      this.loading = true
      const page = isFilter ? 1 : this.current

      this.$http.get('/api/lesson-plan/reports/teacher', {
        params: {
          limit: this.pageSize,
          page,
          session_id: this.selectedSession,
          teacher_id: this.selectedTeacher,
          sorter_field: this.sorterField,
          sorter_order: this.sorterOrder,
          ...this.filters,
        },
      }).then(({ data }) => {
        this.data = data.data
        this.meta = data.meta
        this.loading = false
      }).catch(() => { this.loading = false })
    },

    handleSessionChange(value) {
      this.selectedSession = value
      this.getTeachers()
      this.getReportData(true)
    },

    handleTeacherSelect(value) {
      this.selectedTeacher = value
      this.getReportData(true)
    },

    handleUpdateTable(args) {
      if (args) {
        this.sorterField = args[0]
        this.sorterOrder = args[1]
        this.filters = args[2]
      }
      this.getReportData()
    },

    handleViewDetails(record) {
      this.selectedTeacherForModal = record
      this.logsModalVisible = true
    },

    onShowSizeChange(current, pageSize) {
      this.pageSize = pageSize
      this.current = current
      this.getReportData()
    },

    onChange(current) {
      this.current = current
      this.getReportData()
    },
  },
}
</script>

<style scoped>
.error { padding: 25px 5px; }
</style>
